import { useCallback } from "react";
import { useApiErrorHandler } from "./useApiErrorHandler";
import useToastService from "./useToastService";

/**
 * Custom hook to handle errors comprehensively
 * - First attempts to handle API-specific errors (e.g., 401 Unauthorized)
 * - Falls back to displaying error messages via toast notifications
 */
export function useErrorHandler() {
  const handleApiError = useApiErrorHandler();
  const toastService = useToastService();

  const handleError = useCallback(
    async (error: any) => {
      // First, try to handle as an API error
      const wasHandled = await handleApiError(error);

      // If not handled by API error handler, show toast notification
      if (!wasHandled) {
        const errorMessage = getErrorMessage(error);
        toastService.error(errorMessage);
      }
    },
    [handleApiError, toastService]
  );

  return handleError;
}

/**
 * Extracts a user-friendly error message from various error types
 */
function getErrorMessage(error: any): string {
  // Handle ApiException with message
  if (error?.message) {
    return error.message;
  }

  // Handle error objects with response data
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  // Handle string errors
  if (typeof error === "string") {
    return error;
  }

  // Default fallback message
  return "An unexpected error occurred";
}

export default useErrorHandler;
